import { useState, useEffect, useMemo } from 'react';
import { getUrl } from 'aws-amplify/storage';
import * as Sentry from '@sentry/react';
import { useCostTracking } from '../../contexts/CostTrackingContext';
import { useEquipmentContext } from '../../contexts/EquipmentContext';
import type { ExpenseCategoryEntry } from '../../types/costTrackingTypes';
import { calculateLaborCost, expenseInvoiceLabel, expenseWorkDescriptionLabel, laborHoursLabel, parseLaborEntries, parsePmPartsSnapshot, formatExpenseDateNumeric } from '../../utils/costTrackingUtils';
import { PM_INTERNAL_LABOR_CATEGORY } from '../../utils/pmCostTracking';
import { BackButton } from '../ui';
import '../../css/CostTrackingDashboard.css';
import '../../css/RecordExpenseForm.css';

interface ExpenseRecordDetailProps {
    expenseId: string;
    onBack: () => void;
    backLabel?: string;
}

const formatCurrency = (val: number) =>
    val.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const formatDate = formatExpenseDateNumeric;

/**
 * Read-only detail view of a full ExpenseRecord.
 * Shows all fields: equipment info, service details, PM info, expense categories,
 * labor, attachments, and total expense.
 *
 * Validates: Requirements 8.1, 8.2, 8.3, 11.4
 */
export const ExpenseRecordDetail = ({ expenseId, onBack, backLabel = 'Back to Dashboard' }: ExpenseRecordDetailProps) => {
    const { expenseRecords } = useCostTracking();
    const { companyUnits, companyEquipmentCategories } = useEquipmentContext();
    const [attachmentUrls, setAttachmentUrls] = useState<{ name: string; url: string }[]>([]);

    const record = useMemo(
        () => expenseRecords.find((r) => r.id === expenseId) ?? null,
        [expenseRecords, expenseId],
    );

    // Resolve equipment info
    const unit = useMemo(
        () => (record ? companyUnits.find((u) => u.id === record.unitId) ?? null : null),
        [companyUnits, record],
    );

    const equipmentCategory = useMemo(() => {
        const categoryId = record?.equipmentCategoryId || unit?.categoryId || '';
        if (!categoryId) return null;
        return companyEquipmentCategories.find((c) => c.id === categoryId) ?? null;
    }, [companyEquipmentCategories, record, unit]);

    // Parse expense categories JSON
    const categoryEntries: ExpenseCategoryEntry[] = useMemo(() => {
        if (!record?.expenseCategories) return [];
        try {
            return JSON.parse(record.expenseCategories);
        } catch {
            return [];
        }
    }, [record]);

    const pmPartsSnapshot = useMemo(
        () => (record ? parsePmPartsSnapshot(record.pmPartsSnapshot) : []),
        [record],
    );

    const laborLines = useMemo(
        () => (record ? parseLaborEntries(record.laborEntries) : []),
        [record],
    );
    const laborCost = useMemo(() => {
        if (laborLines.length > 0) return laborLines.reduce((sum, line) => sum + line.cost, 0);
        if (!record) return 0;
        return calculateLaborCost(
            record.laborHoursST || 0,
            record.laborHoursOT || 0,
            record.laborRateST || 0,
            record.laborRateOT || 0,
        );
    }, [record, laborLines]);

    // Fetch attachment download URLs
    useEffect(() => {
        if (!record?.attachments?.length) {
            setAttachmentUrls([]);
            return;
        }

        let cancelled = false;
        const fetchUrls = async () => {
            const urls: { name: string; url: string }[] = [];
            for (const path of record.attachments!) {
                if (!path) continue;
                try {
                    const result = await getUrl({ path });
                    urls.push({
                        name: path.split('/').pop() || path,
                        url: result.url.href,
                    });
                } catch (err) {
                    console.error('Failed to get attachment URL:', err);
                    Sentry.captureException(err);
                    urls.push({ name: path.split('/').pop() || path, url: '' });
                }
            }
            if (!cancelled) setAttachmentUrls(urls);
        };

        fetchUrls();
        return () => { cancelled = true; };
    }, [record]);

    const handlePrint = () => {
        window.print();
    };

    if (!record) {
        return (
            <div className="ct-form-container">
                <BackButton onClick={onBack} buttonText="Back to Dashboard" />
                <div className="ct-table-empty">Expense record not found.</div>
            </div>
        );
    }

    return (
        <div className="ct-form-container ct-detail-view">
            <div className="ct-detail-top-bar no-print">
                <BackButton
                    onClick={onBack}
                    buttonText={backLabel}
                    buttonClasses="back-button--inline"
                />
                <button className="ct-btn-secondary ct-btn-sm" onClick={handlePrint}>
                    🖨 Print
                </button>
            </div>

            <h2 className="ct-form-title">Expense Record Detail</h2>

            {/* ===== Equipment Information ===== */}
            <div className="ct-form-section">
                <h3 className="ct-form-section-title">Equipment Information</h3>
                <div className="ct-detail-grid">
                    <div className="ct-detail-field">
                        <span className="ct-detail-label">Equipment Category</span>
                        <span className="ct-detail-value">{equipmentCategory?.name || '—'}</span>
                    </div>
                    <div className="ct-detail-field">
                        <span className="ct-detail-label">Equipment ID</span>
                        <span className="ct-detail-value">{unit?.name || record.unitId}</span>
                    </div>
                    <div className="ct-detail-field">
                        <span className="ct-detail-label">Description</span>
                        <span className="ct-detail-value">{unit?.description || '—'}</span>
                    </div>
                </div>
            </div>

            {/* ===== Service Details ===== */}
            <div className="ct-form-section">
                <h3 className="ct-form-section-title">Service Details</h3>
                <div className="ct-detail-grid">
                    <div className="ct-detail-field">
                        <span className="ct-detail-label">Date of Input</span>
                        <span className="ct-detail-value">{formatDate(record.dateOfInput)}</span>
                    </div>
                    <div className="ct-detail-field">
                        <span className="ct-detail-label">Date of Service</span>
                        <span className="ct-detail-value">{formatDate(record.dateOfService)}</span>
                    </div>
                    <div className="ct-detail-field">
                        <span className="ct-detail-label">Vendor</span>
                        <span className="ct-detail-value">{record.vendorName || '—'}</span>
                    </div>
                    <div className="ct-detail-field ct-detail-field-wide">
                        <span className="ct-detail-label">Work Description</span>
                        <span className="ct-detail-value">{expenseWorkDescriptionLabel(record.workDescription) || '—'}</span>
                    </div>
                    <div className="ct-detail-field">
                        <span className="ct-detail-label">Invoice / PO / WO #</span>
                        <span className="ct-detail-value">{expenseInvoiceLabel(record.invoiceNumber) || '—'}</span>
                    </div>
                </div>
            </div>

            {/* ===== PM Service Information ===== */}
            {(record.pmServiceLevelId || record.pmServiceLevelName || record.pmExpense) ? (
                <div className="ct-form-section">
                    <h3 className="ct-form-section-title">PM Service Information</h3>
                    <div className="ct-detail-grid">
                        <div className="ct-detail-field">
                            <span className="ct-detail-label">PM Service Level</span>
                            <span className="ct-detail-value">{record.pmServiceLevelName || '—'}</span>
                        </div>
                        {(record.pmExpense || 0) > 0 && (
                            <div className="ct-detail-field">
                                <span className="ct-detail-label">PM Expense</span>
                                <span className="ct-detail-value">{formatCurrency(record.pmExpense || 0)}</span>
                            </div>
                        )}
                    </div>
                    {pmPartsSnapshot.length > 0 && (
                        <div className="ct-detail-categories-table">
                            <div className="ct-detail-cat-header">
                                <span>Item (price at entry)</span>
                                <span>Cost</span>
                            </div>
                            {pmPartsSnapshot.map((line) => (
                                <div key={line.serviceItemId || line.itemName} className="ct-detail-cat-row">
                                    <span>
                                        {line.itemName}
                                        {line.quantity > 1 ? ` × ${line.quantity}` : ''}
                                    </span>
                                    <span>{formatCurrency(line.lineTotal)}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            ) : null}

            {/* ===== Expense Categories ===== */}
            {categoryEntries.length > 0 && (
                <div className="ct-form-section">
                    <h3 className="ct-form-section-title">Expense Categories</h3>
                    <div className="ct-detail-categories-table">
                        <div className="ct-detail-cat-header">
                            <span>Category</span>
                            <span>Cost</span>
                        </div>
                        {categoryEntries.map((entry, idx) => (
                            <div key={idx} className="ct-detail-cat-row">
                                <span>{entry.categoryName}</span>
                                <span>{formatCurrency(entry.cost)}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* ===== Labor ===== */}
            {(laborLines.length > 0 || record.laborRateScheduleId || laborCost > 0) ? (
                <div className="ct-form-section">
                    <h3 className="ct-form-section-title">Labor</h3>
                    {laborLines.length > 0 ? (
                        <div className="ct-detail-categories-table">
                            <div className="ct-detail-cat-header">
                                <span>Level</span>
                                <span>Hours</span>
                                <span>Amount</span>
                            </div>
                            {laborLines.map((line, idx) => (
                                <div key={`${line.categoryId}-${idx}`} className="ct-detail-cat-row">
                                    <span>{line.categoryName || 'Hours only'}</span>
                                    <span>{laborHoursLabel(line.hoursST, line.hoursOT) || '—'}</span>
                                    <span>{line.cost > 0 ? formatCurrency(line.cost) : 'Hours only'}</span>
                                </div>
                            ))}
                        </div>
                    ) : (
                    <div className="ct-detail-grid">
                        <div className="ct-detail-field">
                            <span className="ct-detail-label">Labor Rate Schedule</span>
                            <span className="ct-detail-value">{record.laborRateScheduleName || '—'}</span>
                        </div>
                        <div className="ct-detail-field">
                            <span className="ct-detail-label">ST Hours</span>
                            <span className="ct-detail-value">{record.laborHoursST || 0}</span>
                        </div>
                        <div className="ct-detail-field">
                            <span className="ct-detail-label">OT Hours</span>
                            <span className="ct-detail-value">{record.laborHoursOT || 0}</span>
                        </div>
                        <div className="ct-detail-field">
                            <span className="ct-detail-label">ST Rate</span>
                            <span className="ct-detail-value">{formatCurrency(record.laborRateST || 0)}/hr</span>
                        </div>
                        <div className="ct-detail-field">
                            <span className="ct-detail-label">OT Rate</span>
                            <span className="ct-detail-value">{formatCurrency(record.laborRateOT || 0)}/hr</span>
                        </div>
                        <div className="ct-detail-field">
                            <span className="ct-detail-label">Total Labor Cost</span>
                            <span className="ct-detail-value ct-detail-value-bold">{formatCurrency(laborCost)}</span>
                        </div>
                    </div>
                    )}
                </div>
            ) : null}

            {/* ===== Attachments ===== */}
            {attachmentUrls.length > 0 && (
                <div className="ct-form-section">
                    <h3 className="ct-form-section-title">Attachments</h3>
                    <ul className="ct-attachment-list">
                        {attachmentUrls.map((att, idx) => (
                            <li key={idx} className="ct-attachment-item">
                                <span className="ct-attachment-name">{att.name}</span>
                                {att.url ? (
                                    <a
                                        href={att.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="ct-detail-download-link"
                                    >
                                        Download
                                    </a>
                                ) : (
                                    <span className="ct-detail-download-unavailable">Unavailable</span>
                                )}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* ===== Total Expense Summary ===== */}
            <div className="ct-form-section ct-total-summary">
                <h3 className="ct-form-section-title">Total Expense</h3>
                <div className="ct-total-breakdown">
                    {(record.pmExpense || 0) > 0 && (
                        <div className="ct-total-row">
                            <span>PM Expense</span>
                            <span>{formatCurrency(record.pmExpense || 0)}</span>
                        </div>
                    )}
                    {categoryEntries.map((entry, idx) => (
                        <div key={idx} className="ct-total-row">
                            <span>{entry.categoryName}</span>
                            <span>{formatCurrency(entry.cost)}</span>
                        </div>
                    ))}
                    {laborCost > 0 && !categoryEntries.some((entry) => entry.categoryName === PM_INTERNAL_LABOR_CATEGORY) && (
                        <div className="ct-total-row">
                            <span>Labor</span>
                            <span>{formatCurrency(laborCost)}</span>
                        </div>
                    )}
                    <div className="ct-total-row ct-total-row-grand">
                        <span>Total</span>
                        <span>{formatCurrency(record.totalExpense)}</span>
                    </div>
                </div>
            </div>
        </div>
    );
};
